<template>
  <div class="summary-container" v-if="show">
    <div class="summary-modal">
      <div class="summary-header">
        <h3 v-if="viewMode === 'summary'">Offtake Suitability Summary</h3>
        <h3 v-else-if="viewMode === 'select-facility'">Select Biochar Facility</h3>
        <h3 v-else>Application Upside</h3>
        <button @click="closeSummary" class="close-button" title="Close">×</button>
      </div>

      <div class="summary-content">
        <!-- Loading State for Entire Component -->
        <div v-if="loading" class="loading-overlay">
          <div class="loading-content">
            <b-spinner variant="success" label="Loading..."></b-spinner>
            <p>Analyzing Offtake Suitability...</p>
          </div>
        </div>

        <!-- Initial Summary Content -->
        <div v-if="viewMode === 'summary' && !loading">
          <p><strong>Number of Commercial Farms:</strong> {{ commercialFarmCount }}</p>
          <p><strong>Number of Maize Farms:</strong> {{ smallFarmCount }}</p>
          <p><strong>Area Available for Biochar (hectares):</strong> {{ totalFarmArea }}</p>
          <p><strong>% of Area Used:</strong> {{ percentAreaUsed }}%</p>
          <div class="soil-analysis">
            <h4><strong>Soil Analysis (within 50km)</strong></h4>
            <p>
              <strong>Soil pH:</strong>
              {{ soilAnalysis.pH.average !== 'N/A' ? soilAnalysis.pH.average : 'N/A' }}
              <span v-if="soilAnalysis.pH.average !== 'N/A'">
                (Min: {{ soilAnalysis.pH.min }}, Max: {{ soilAnalysis.pH.max }})
              </span>
            </p>
            <p>
              <strong>Sand Texture (%):</strong>
              {{ soilAnalysis.sandTexture.average !== 'N/A' ? soilAnalysis.sandTexture.average : 'N/A' }}
              <span v-if="soilAnalysis.sandTexture.average !== 'N/A'">
                (Min: {{ soilAnalysis.sandTexture.min }}, Max: {{ soilAnalysis.sandTexture.max }})
              </span>
            </p>
            <p>
              <strong>Soil Carbon (g/kg):</strong>
              {{ soilAnalysis.soilCarbon.average !== 'N/A' ? soilAnalysis.soilCarbon.average : 'N/A' }}
              <span v-if="soilAnalysis.soilCarbon.average !== 'N/A'">
                (Min: {{ soilAnalysis.soilCarbon.min }}, Max: {{ soilAnalysis.soilCarbon.max }})
              </span>
            </p>
            <p><strong>Biochar Recommendation:</strong> {{ soilAnalysis.biocharRecommendation }}</p>
          </div>
          <button @click="viewMode = 'select-facility'" class="upside-button">
            Get Application Upside
          </button>
        </div>

        <!-- Select Biochar Facility -->
        <div v-if="viewMode === 'select-facility' && !loading" class="facility-selection">
          <button @click="viewMode = 'summary'" class="back-button">← Back</button>
          <div class="facility-dropdown">
            <label for="biocharFacility">Select Biochar Facility:</label>
            <div v-if="facilityLoading" class="dropdown-loading">
              <b-spinner small variant="success" label="Loading Facilities..."></b-spinner>
              <span>Loading facilities...</span>
            </div>
            <select
              v-else
              id="biocharFacility"
              v-model="selectedFacility"
              @change="onFacilitySelect"
              :disabled="facilityLoading || biocharFacilities.length === 0"
            >
              <option value="" disabled>Select a facility</option>
              <option v-for="facility in biocharFacilities" :key="facility.id" :value="facility.id">
                {{ facility.name }}
              </option>
            </select>
            <button
              v-if="selectedFacility"
              @click="showApplicationUpside"
              class="upside-button"
              :disabled="facilityLoading"
            >
              View Application Upside
            </button>
          </div>
        </div>

        <!-- Application Upside Content -->
        <div v-if="viewMode === 'upside' && !loading">
          <button @click="viewMode = 'select-facility'" class="back-button">← Back</button>
          <div class="upside-content">
            <h4>Application Upside for {{ selectedFacilityName }}</h4>
            <p><strong>Facility Output Rate:</strong> {{ selectedFacilityOutput }}</p>
            <p><strong>Soil Quality Gain:</strong> {{ upsideData.soilQualityGain }}</p>
            <p><strong>Estimated Crop Yield:</strong> {{ upsideData.estimatedCropYield }}</p>
            <p><strong>Potential Carbon Credit Earnings:</strong> {{ upsideData.carbonCreditEarnings }}</p>
            <p><strong>Overall Upside:</strong> {{ upsideData.overallUpside }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OfftakeSuitabilitySummary',
  props: {
    show: Boolean,
    commercialFarmCount: Number,
    smallFarmCount: Number,
    totalFarmArea: Number,
    selectedBufferArea: Number,
    percentAreaUsed: String,
    soilAnalysis: Object,
    loading: Boolean,
    biocharFacilities: Array, 
    facilityLoading: Boolean,
  },
  data() {
    return {
      viewMode: 'summary', 
      selectedFacility: '', 
      selectedFacilityName: '', 
      selectedFacilityOutput: '', 
      upsideData: {
        soilQualityGain: '',
        estimatedCropYield: '',
        carbonCreditEarnings: '',
        overallUpside: '',
      },
    };
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.viewMode = 'summary';
        this.selectedFacility = '';
        this.selectedFacilityName = '';
        this.selectedFacilityOutput = '';
        this.resetUpsideData();
      }
    },
  },
  methods: {
    onFacilitySelect() {
      const facility = this.biocharFacilities.find((f) => f.id === this.selectedFacility);
      if (facility) {
        this.selectedFacilityName = facility.name;
        this.selectedFacilityOutput = facility.output;
        // Generate dummy Upside data based on output
        this.upsideData = {
          soilQualityGain: `${15 + (parseFloat(facility.output) || 0) * 0.5}% improvement in nutrient retention`,
          estimatedCropYield: `${20 + (parseFloat(facility.output) || 0) * 0.8}% increase in maize yield`,
          carbonCreditEarnings: `$${5000 + (parseFloat(facility.output) || 0) * 1000}/year potential earnings`,
          overallUpside: 'High ROI with sustainable benefits',
        };
      } else {
        this.selectedFacilityName = '';
        this.selectedFacilityOutput = '';
        this.resetUpsideData();
      }
    },
    showApplicationUpside() {
      if (this.selectedFacility) {
        this.viewMode = 'upside';
      }
    },
    resetUpsideData() {
      this.upsideData = {
        soilQualityGain: '',
        estimatedCropYield: '',
        carbonCreditEarnings: '',
        overallUpside: '',
      };
    },
    closeSummary() {
      this.$emit('close');
      this.viewMode = 'summary';
      this.selectedFacility = '';
      this.selectedFacilityName = '';
      this.selectedFacilityOutput = '';
      this.resetUpsideData();
    },
  },
};
</script>

<style scoped>
.summary-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  z-index: 1000;
  pointer-events: none;
}

.summary-modal {
  background: white;
  padding: 20px;
  border-radius: 5px;
  box-shadow: 0 3rem 5rem rgba(0, 0, 0, 0.3);
  text-align: left;
  width: 350px;
  margin: 20px;
  max-width: 500px;
  pointer-events: auto;
  position: relative;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.summary-header h3 {
  margin: 0;
  font-size: 1.2rem;
}

.summary-header button.close-button {
  background: none;
  border: none;
  color: #000000;
  font-size: 2rem;
  line-height: 1;
  padding: 0;
  cursor: pointer;
  transition: color 0.2s;
}


.summary-content p {
  margin: 5px 0;
}

.soil-analysis h4 {
  margin: 10px 0 5px;
  font-size: 1rem;
}

.soil-analysis p span {
  color: #666;
  font-size: 0.9rem;
}

.facility-selection {
  margin-bottom: 15px;
}

.facility-dropdown {
  margin: 15px 0;
}

.facility-dropdown label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.facility-dropdown select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-bottom: 10px;
}

.dropdown-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  color: #333;
}

.dropdown-loading span {
  font-size: 0.9rem;
}

.upside-button {
  background-color: #4caf51;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  width: 100%;
  margin-top: 10px;
}

.upside-button:hover {
  background-color: #3E8E41;
}

.upside-button:disabled {
  background-color: #90caf9;
  cursor: not-allowed;
}

.back-button {
  background-color: #999999;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 3px;
  cursor: pointer;
  margin-bottom: 15px;
}

.back-button:hover {
  background-color: #222222;
}

.upside-content h4 {
  margin: 10px 0 5px;
  font-size: 1rem;
  font-weight: 600;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  border-radius: 5px;
}

.loading-content {
  text-align: center;
}

.loading-content p {
  margin-top: 10px;
  color: #333;
}
</style>